/* `/spaces/{agent_id}` — an agent's own tables, their columns and rules, their rows, and the
   plan of a change to them. `backend/modules/spaces/README.md` for the wire.

   In `shared/api/` because the agent's Spaces tab reads it, the chat's approval card shows
   Builder's planned changes with it, and `events.ts` refreshes it when rows change — and
   `shared/` may not import a feature (rule book §1 rule 5).

   Paths are bare; `client.baseURL` carries `/api/v1`. Every response goes through `unwrap`. */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";

import type {
  SpaceChange,
  SpacePlan,
  SpaceRepeatGroup,
  SpaceRowDetail,
  SpaceRowResult,
  SpaceRowsPage,
  SpaceRowsQuery,
  SpaceTable,
  SpaceTemplate,
} from "@/shared/types";

export const spaceKeys = {
  all: ["spaces"] as const,
  agent: (agentId: string) => [...spaceKeys.all, agentId] as const,
  tables: (agentId: string) => [...spaceKeys.agent(agentId), "tables"] as const,
  rows: (agentId: string) => [...spaceKeys.agent(agentId), "rows"] as const,
  page: (agentId: string, tableId: string, query: SpaceRowsQuery) =>
    [...spaceKeys.rows(agentId), tableId, query] as const,
  row: (agentId: string, tableId: string, rowId: string) =>
    [...spaceKeys.rows(agentId), tableId, "row", rowId] as const,
  repeats: (agentId: string, tableId: string, column: string) =>
    [...spaceKeys.rows(agentId), tableId, "repeats", column] as const,
  plan: (agentId: string, changes: SpaceChange[]) => [...spaceKeys.agent(agentId), "plan", changes] as const,
  templates: () => [...spaceKeys.all, "templates"] as const,
};

/* ------------------------------ endpoints -------------------------------- */

const base = (agentId: string) => `/spaces/${agentId}`;
const rowsPath = (agentId: string, tableId: string) => `${base(agentId)}/tables/${tableId}/rows`;

/** The agent's tables, People first, each with its columns, rules and row count. */
export async function listTables(agentId: string): Promise<SpaceTable[]> {
  return unwrap(await client.get<Envelope<SpaceTable[]>>(base(agentId)));
}

/** One page of a table's rows; People pages on `before` instead of `offset`. */
export async function listRows(agentId: string, tableId: string, query: SpaceRowsQuery): Promise<SpaceRowsPage> {
  const params = { ...query, where: query.where?.length ? JSON.stringify(query.where) : undefined };
  return unwrap(await client.get<Envelope<SpaceRowsPage>>(rowsPath(agentId, tableId), { params }));
}

/** One row with the rows linking to it and its activity. */
export async function getRow(agentId: string, tableId: string, rowId: string): Promise<SpaceRowDetail> {
  return unwrap(await client.get<Envelope<SpaceRowDetail>>(`${rowsPath(agentId, tableId)}/${rowId}`));
}

export interface RowWrite {
  values: Record<string, unknown>;
  verified_by?: Record<string, string>;
}

/** Each row checked on its own: what became of each, in order. */
export async function addRows(agentId: string, tableId: string, rows: RowWrite[]): Promise<SpaceRowResult[]> {
  return unwrap(await client.post<Envelope<SpaceRowResult[]>>(rowsPath(agentId, tableId), { rows }));
}

/** Only the values sent change; null clears a cell. */
export async function updateRows(
  agentId: string,
  tableId: string,
  rows: (RowWrite & { id: string })[],
): Promise<SpaceRowResult[]> {
  return unwrap(await client.patch<Envelope<SpaceRowResult[]>>(rowsPath(agentId, tableId), { rows }));
}

export async function deleteRows(agentId: string, tableId: string, ids: string[]): Promise<{ deleted: number }> {
  const response = await client.delete<Envelope<{ deleted: number }>>(rowsPath(agentId, tableId), {
    params: { ids },
    paramsSerializer: { indexes: null }, // ids=a&ids=b, as FastAPI reads a list
  });
  return unwrap(response);
}

/** Rows sharing a value of one column, as a unique rule compares it: biggest group first. */
export async function listRepeats(agentId: string, tableId: string, column: string): Promise<SpaceRepeatGroup[]> {
  const path = `${base(agentId)}/tables/${tableId}/repeats`;
  return unwrap(await client.get<Envelope<{ groups: SpaceRepeatGroup[] }>>(path, { params: { column } })).groups;
}

/** What a change set would do, step by step, without doing it. */
export async function planChanges(agentId: string, changes: SpaceChange[]): Promise<SpacePlan> {
  return unwrap(await client.post<Envelope<SpacePlan>>(`${base(agentId)}/changes/plan`, { changes }));
}

/** Make a change set; 409 when its plan differs from the `digest` shown. Without a digest it
    is made as planned now: only for a change no row decides, like moving a column. */
export async function applyChanges(agentId: string, changes: SpaceChange[], digest?: string): Promise<SpacePlan> {
  return unwrap(await client.post<Envelope<SpacePlan>>(`${base(agentId)}/changes`, { changes, digest }));
}

export async function listTemplates(): Promise<SpaceTemplate[]> {
  return unwrap(await client.get<Envelope<SpaceTemplate[]>>("/spaces/templates"));
}

/* -------------------------------- hooks ---------------------------------- */

export const useSpaceTables = (agentId: string) =>
  useQuery({ queryKey: spaceKeys.tables(agentId), queryFn: () => listTables(agentId) });

/** The page stays on screen while the next one loads: paging and sorting never blank the table. */
export const useSpaceRows = (agentId: string, tableId: string | null, query: SpaceRowsQuery) =>
  useQuery({
    queryKey: spaceKeys.page(agentId, tableId ?? "", query),
    queryFn: () => listRows(agentId, tableId as string, query),
    enabled: !!tableId,
    placeholderData: keepPreviousData,
  });

export const useSpaceRow = (agentId: string, tableId: string, rowId: string | null) =>
  useQuery({
    queryKey: spaceKeys.row(agentId, tableId, rowId ?? ""),
    queryFn: () => getRow(agentId, tableId, rowId as string),
    enabled: !!rowId,
  });

/** A plan is a read: it is fetched again whenever the changes differ. */
export const useSpacePlan = (agentId: string, changes: SpaceChange[] | null) =>
  useQuery({
    queryKey: spaceKeys.plan(agentId, changes ?? []),
    queryFn: () => planChanges(agentId, changes as SpaceChange[]),
    enabled: !!changes?.length,
    placeholderData: keepPreviousData, // the last plan stays while the next is checked: no jump
    retry: false, // a blocked change set answers 422 with why: show it, don't ask again
    meta: { silent: true }, // the plan's screen shows the refusal in place
  });

/** Asked only while a unique rule is blocked by them. */
export const useSpaceRepeats = (agentId: string, tableId: string, column: string | null) =>
  useQuery({
    queryKey: spaceKeys.repeats(agentId, tableId, column ?? ""),
    queryFn: () => listRepeats(agentId, tableId, column as string),
    enabled: !!column,
  });

export const useSpaceTemplates = () => useQuery({ queryKey: spaceKeys.templates(), queryFn: listTemplates });

/** Writes refresh the agent's whole space: a count, a link label or the rail may show it.
    Failures reach the global toast (queryClient.ts). */
function useSpaceWrite<A, R>(agentId: string, mutationFn: (args: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn, onSuccess: () => qc.invalidateQueries({ queryKey: spaceKeys.agent(agentId) }) });
}

export const useAddRows = (agentId: string, tableId: string) =>
  useSpaceWrite(agentId, (rows: RowWrite[]) => addRows(agentId, tableId, rows));
export const useUpdateRows = (agentId: string, tableId: string) =>
  useSpaceWrite(agentId, (rows: (RowWrite & { id: string })[]) => updateRows(agentId, tableId, rows));
export const useDeleteRows = (agentId: string, tableId: string) =>
  useSpaceWrite(agentId, (ids: string[]) => deleteRows(agentId, tableId, ids));
export const useApplyChanges = (agentId: string) =>
  useSpaceWrite(agentId, ({ changes, digest }: { changes: SpaceChange[]; digest?: string }) =>
    applyChanges(agentId, changes, digest),
  );
